/* eslint-disable indent */
/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

/**
 * NumberScrollLock - Prevents mouse wheel events from incrementing/decrementing
 * the value of number inputs. This is enabled by default for all Number and
 * PreciseNumber inputs to avoid accidental value changes while scrolling.
 *
 * To re-enable mouse wheel scrolling on a specific input, set the following
 * in the PictForm configuration for that input:
 *
 *   "AllowMouseWheelScroll": true
 */
class NumberScrollLock extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Attach a wheel event listener to the input element that prevents
	 * scroll-based value changes while the input is focused.
	 *
	 * @param {HTMLInputElement} pElement - The input element to protect.
	 */
	attachScrollLock(pElement)
	{
		if (!pElement) return;

		pElement.addEventListener('wheel', (pEvent) =>
		{
			// Only prevent the default behavior when the input is focused,
			// so that normal page scrolling is unaffected when the input
			// is not active.
			if (document.activeElement === pElement)
			{
				pEvent.preventDefault();
			}
		}, { passive: false });
	}

	/**
	 * Called when a standard (non-tabular) input is initialized.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group definition object.
	 * @param {Object} pRow - The row index.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value of the input.
	 * @param {string} pHTMLSelector - The HTML selector for the input.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		// Allow opting back in to wheel scroll via PictForm config
		if (pInput?.PictForm?.AllowMouseWheelScroll)
		{
			return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}

		const tmpElement = this.pict.ContentAssignment.getElement(pHTMLSelector)?.[0];
		this.attachScrollLock(tmpElement);

		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Called when a tabular input is initialized.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group definition object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value of the input.
	 * @param {string} pHTMLSelector - The HTML selector for the input.
	 * @param {number} pRowIndex - The row index.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		// Allow opting back in to wheel scroll via PictForm config
		if (pInput?.PictForm?.AllowMouseWheelScroll)
		{
			return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}

		// Append the row-specific data attribute to the base selector.
		// The base pHTMLSelector does not include the row index, so we
		// match the pattern used by the view's dataChangedTabular flow.
		const tmpRowSelector = pHTMLSelector + `[data-i-index="${pRowIndex}"]`;
		const tmpElement = this.pict.ContentAssignment.getElement(tmpRowSelector)?.[0];
		this.attachScrollLock(tmpElement);

		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}
}

module.exports = NumberScrollLock;
